"use client";

import React from "react";
import { CheckIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import LoadingSpinner from "@/components/ui/loading-spinner";
import { PresetOption } from "@/types/createAgent";
import {
  ActionOptionVisual,
  hasActionOptionVisual,
} from "../visuals/ActionOptionVisuals";

export function PresetGridTile({
  preset,
  isProcessing,
  isDisabled,
  isSelected,
  onClick,
}: {
  preset: PresetOption;
  isProcessing: boolean;
  isDisabled: boolean;
  isSelected: boolean;
  onClick: () => void;
}) {
  const hasVisual = hasActionOptionVisual(preset);
  const visualClassName =
    "flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted/40";
  const selectedIndicator = isSelected ? (
    <span
      data-slot="preset-selection-indicator"
      aria-hidden="true"
      className="agent-preset-selection-indicator absolute right-1 top-1 grid size-5 place-items-center rounded-full bg-brand-logo text-brand-logo-foreground"
    >
      <CheckIcon size={13} weight="bold" />
    </span>
  ) : null;

  // Options without a rendered swatch/sprite lean on a bold, legible label —
  // never a generic placeholder glyph in a grey box.
  if (!hasVisual && !isProcessing) {
    return (
      <button
        type="button"
        aria-pressed={isSelected}
        onClick={() => !isDisabled && onClick()}
        disabled={isDisabled}
        className={cn(
          "group/action relative flex min-h-[100px] min-w-0 touch-manipulation flex-col items-center justify-center gap-1 rounded-xl border p-2.5 text-center transition-[background-color,border-color,transform] duration-200 motion-safe:active:scale-[0.985] motion-reduce:transition-none",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          "border-border bg-card hover:border-brand-logo/40 hover:bg-muted/35",
          isSelected &&
            "border-brand-logo/50 bg-brand-logo/10 opacity-100 hover:border-brand-logo/50 hover:bg-brand-logo/10",
          isDisabled &&
            !isSelected &&
            "cursor-default opacity-50 hover:border-border hover:bg-card"
        )}
      >
        {selectedIndicator}
        <span className="block w-full min-w-0 text-sm font-semibold leading-snug text-foreground [overflow-wrap:anywhere]">
          {preset.label}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      onClick={() => !isDisabled && onClick()}
      disabled={isDisabled}
      className={cn(
        "group/action relative flex min-h-[100px] min-w-0 touch-manipulation flex-col items-center justify-start gap-1.5 rounded-xl border p-2 text-center transition-[background-color,border-color,transform] duration-200 motion-safe:active:scale-[0.985] motion-reduce:transition-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "border-border bg-card hover:border-brand-logo/40 hover:bg-muted/35",
        isSelected &&
          "border-brand-logo/50 bg-brand-logo/10 opacity-100 hover:border-brand-logo/50 hover:bg-brand-logo/10",
        isDisabled &&
          !isSelected &&
          "cursor-default opacity-50 hover:border-border hover:bg-card"
      )}
    >
      {selectedIndicator}
      {isProcessing ? (
        <span data-preset-grid-visual className={visualClassName}>
          <LoadingSpinner size="sm" color="gray" className="shrink-0" />
        </span>
      ) : (
        <ActionOptionVisual preset={preset} className={visualClassName} />
      )}
      <span className="block w-full min-w-0 text-[13px] font-semibold leading-tight text-foreground [overflow-wrap:anywhere]">
        {isProcessing ? "Updating..." : preset.label}
      </span>
    </button>
  );
}
